import React, { useState } from 'react';
import { Popover } from 'antd';
import NAV_DESTINATIONS, { isCurrentRoute, slugify } from './navDestinations';

// Phone navigation (<640px).
//
// The header cannot hold six destinations and the Rain Hold / Deploy actions on
// a 360px screen — the old two-row header pushed the actions to row 1 and left a
// cramped icon strip on row 2. Here the destinations move to a fixed bar at the
// bottom of the viewport, in thumb reach, and the header goes back to one row.
//
// The bar is `position: fixed` (Navigation renders inside <header
// class="app-header"> and must not restructure the shell); the matching
// `@media (max-width: 639px)` block in Navigation.css gives .app-content a
// padding-bottom of exactly the bar's height, so the bar overlays nothing.
//
// Rendered only when useBreakpoint() === 'compact', so it is absent from the DOM
// at every other tier rather than merely hidden.
// `destinations` defaults to the full set; Navigation passes its capability-filtered list so
// this bar and the header menu always agree on what a tenant can see.
const BottomTabBar = ({ pathname, onNavigate, destinations = NAV_DESTINATIONS }) => {
  // Which destination's sub-item sheet is open (only Schedule has one).
  const [openKey, setOpenKey] = useState(null);

  const renderTab = (destination) => {
    const { key, label, Icon, children } = destination;
    const testId = `bottom-tab-${slugify(label)}`;
    const isCurrent = isCurrentRoute(key, pathname);

    const tab = (
      <button
        type="button"
        className="bottom-tab"
        data-testid={testId}
        // Icon-only targets, exactly like the strip they replace: the name has
        // to come from aria-label.
        aria-label={label}
        aria-current={isCurrent ? 'page' : undefined}
        // Parents open their sheet instead of navigating, and antd's trigger
        // owns that click — adding an onClick here would toggle it twice.
        onClick={children ? undefined : () => onNavigate(key)}
      >
        <Icon className="bottom-tab-icon" />
      </button>
    );

    if (!children) return React.cloneElement(tab, { key });

    return (
      <Popover
        key={key}
        open={openKey === key}
        onOpenChange={(visible) => setOpenKey(visible ? key : null)}
        trigger="click"
        // Above the bar: the trigger sits at the bottom edge of the viewport.
        placement="top"
        destroyTooltipOnHide
        overlayClassName="bottom-tab-sheet"
        content={
          <div role="menu" aria-label={label} data-testid={`${testId}-menu`}>
            {children.map((child) => (
              <button
                type="button"
                role="menuitem"
                key={child.key}
                className="bottom-tab-sheet-item"
                onClick={() => {
                  setOpenKey(null);
                  onNavigate(child.key);
                }}
              >
                {child.label}
              </button>
            ))}
          </div>
        }
      >
        {tab}
      </Popover>
    );
  };

  return (
    <nav className="bottom-tab-bar" data-testid="bottom-tab-bar" aria-label="Primary">
      {destinations.map(renderTab)}
    </nav>
  );
};

export default BottomTabBar;
